import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaLinkedin,
  FaFacebook,
  FaGithub,
  FaPaperPlane,
  FaCheckCircle,
  FaRocket
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import SEO from '../components/SEO/SEO';
import contentData from '../data/contentData.json';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    service: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const formRef = useRef();

  // SEO data for Contact page
  const seoData = {
    title: "Contact Us - Get Expert Cloud Consulting",
    description: `Contact ${contentData.company.name} for expert cloud consulting and solutions. Get in touch with our AWS, Azure, and GCP specialists. Call ${contentData.company.contact.phone} or email ${contentData.company.contact.email}.`,
    keywords: "Contact Dhyon Technologies, Cloud Consulting Contact, AWS Experts Contact, Cloud Migration Consultation, DevOps Consulting"
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
        subject: '',
      message: '',
        service: ''
    });
    }, 3000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };

  return (
    <>
      <SEO {...seoData} />
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-0">
          <motion.div 
            className="absolute top-20 left-10 w-64 h-64 bg-blue-500/10 rounded-full mix-blend-multiply filter blur-xl"
            variants={floatingVariants}
            animate="animate"
          />
          <motion.div 
            className="absolute top-40 right-20 w-72 h-72 bg-purple-500/10 rounded-full mix-blend-multiply filter blur-xl"
            variants={floatingVariants}
            animate="animate"
            style={{ animationDelay: '2s' }}
          />
          <motion.div 
            className="absolute bottom-20 left-20 w-80 h-80 bg-cyan-500/10 rounded-full mix-blend-multiply filter blur-xl"
            variants={floatingVariants}
            animate="animate"
            style={{ animationDelay: '4s' }}
          />
        </div>

        <div className="relative z-10 pt-32 pb-16">
          <div className="container mx-auto px-4">
            {/* Header */}
            <motion.div 
              className="text-center mb-16"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.h1 
                className="text-5xl md:text-6xl font-bold mb-6"
                variants={itemVariants}
              >
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent animate-gradient-x">
                  Get In Touch
                </span>
              </motion.h1>
              <motion.p 
                className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
                variants={itemVariants}
              >
                Ready to accelerate your growth with cloud innovation, software excellence, and startup enablement? Let’s build your future together.
              </motion.p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-16">
              {/* Contact Information */}
              <motion.div 
                className="space-y-8"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.div variants={itemVariants}>
                  <h2 className="text-3xl font-bold text-gray-900 mb-8">
                    Let's Start a Conversation
                  </h2>
                  <p className="text-gray-600 text-lg leading-relaxed mb-8">
                    We're here to help you navigate your cloud journey. Reach out to our team of experts for personalized solutions tailored to your business needs.
                </p>
                </motion.div>

                {/* Contact Cards */}
                <div className="space-y-6">
                  {/* Phone */}
                  <motion.div 
                    className="group p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <FaPhone className="text-white text-xl rotate-90" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-1">Call Us</h3>
                        <div className="space-y-1">
                          <p className="text-gray-600">
                            <span className="font-medium text-gray-700">Ahmedabad Office:</span> <a href="tel:+918320329478" className="hover:text-blue-600 transition-colors">+91 83203 29478</a>
                          </p>
                          <p className="text-gray-600">
                            <span className="font-medium text-gray-700">UAE Office:</span> <a href="tel:+971586229478" className="hover:text-blue-600 transition-colors">+97 1586229478</a>
                          </p>
                        </div>
                        <p className="text-sm text-blue-600 font-medium mt-2">Available 24/7</p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Email */}
                  <motion.div 
                    className="group p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <FaEnvelope className="text-white text-xl" />
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-1">Email Us</h3>
                        <p className="text-gray-600">{contentData.company.contact.email}</p>
                        <p className="text-gray-600">{contentData.company.contact.email1}</p>
                        <p className="text-sm text-blue-600 font-medium">Response within 2 hours</p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Offices */}
                  {contentData.company.locations.map((location, index) => (
                    <motion.div 
                      key={index}
                      className="group p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                      variants={itemVariants}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <FaMapMarkerAlt className="text-white text-xl" />
                    </div>
                    <div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-1">{location.city} Office</h3>
                          <p className="text-gray-600">{location.address}</p>
                          <p className="text-sm text-blue-600 font-medium">Business Hours: 9:00 AM - 6:00 PM</p>
                    </div>
                  </div>
                    </motion.div>
                  ))}
              </div>
              
                {/* Social Links */}
                <motion.div 
                  className="pt-8"
                  variants={itemVariants}
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Follow Us</h3>
                  <div className="flex space-x-4">
                    {[
                      { icon: FaLinkedin, href: 'https://www.linkedin.com/company/dhyon-technologies/', color: 'from-blue-600 to-blue-700' },
                      { icon: FaXTwitter, href: 'https://x.com/DhyonTech', color: 'from-gray-800 to-black' },
                    ].map((social, index) => (
                      <motion.a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-12 h-12 bg-gradient-to-r ${social.color} rounded-xl flex items-center justify-center text-white hover:shadow-lg transition-all duration-300`}
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <social.icon className="text-lg" />
                      </motion.a>
                    ))}
                  </div>
                </motion.div>
              </motion.div>

              {/* Contact Form */}
              <motion.div 
                className="relative"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/50">
                  {/* Floating decorations */}
                  <div className="absolute -top-4 -right-4 w-8 h-8 bg-blue-400 rounded-full animate-ping opacity-20"></div>
                  <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-purple-400 rounded-full animate-pulse opacity-30"></div>

                  <iframe width="500" height="850" src="https://dhyon.ezboss.in/forms/quote/8c5f716b864bc629120d7ef1c374d584" frameborder="0" sandbox="allow-top-navigation allow-forms allow-scripts allow-same-origin allow-popups" allowfullscreen></iframe>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;